import Button from '@/components/Button';
import Panel from '@/components/panel/Panel';
import { SlRefresh } from '@/components/react-icons/main';
import { action } from '@/lib/api.js';
import openOauthWindow from '@/lib/openOauthWindow';
import sendToast from '@/lib/sendToast';
import { getCredentialById, getM2MOauthToken } from '@/redux/actions/gravityActions';
import setPref from '@/redux/actions/uiActions/setPref';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import selectors from '@/redux/selectors/selectors.js';
import { generateDeepDiffs } from '@shared/utils/generateDeepDiffs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import getAuthByCredential from 'shared/utils/getAuthByCredential.js';

const CredentialButtons = ({ app = {}, credential = {}, credentialsRef, loading = false, previousValue, currentValue, onTest, onSetActiveCredentialId, autoConnectOAuth = true }) => {
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const companyId = useSelector(selectors.companyId);
  const hasBeenModified = useMemo(() => (generateDeepDiffs(previousValue, currentValue) || []).length > 0, [previousValue, currentValue]);

  const appSlug = app?.slug;
  const appName = app?.name;
  const appVersion = app?.version;
  const appAuthentication = app?.authentication;
  const appAuth = getAuthByCredential({ authentication: appAuthentication, credential });
  const requiredArgs = appAuth?.arguments?.filter((n) => n.required);

  const isOAuth2 = credential?.meta?.type === 'oAuth2';
  const oAuth2Object = isOAuth2 ? appAuthentication?.find((n) => n.type === 'oAuth2') : {};
  const isM2M = isOAuth2 && oAuth2Object?.options?.enableM2M;
  const oauth2Args = isOAuth2 ? oAuth2Object?.arguments || [] : [];
  const requiredOAuth2Args = oauth2Args.filter((n) => n.required);
  const isConnect = isOAuth2 && (!credential?.status || credential?.status === 'unknown' || credential?.status === 'incomplete');
  const showReconnectButton = isOAuth2 && !isConnect;

  const hasTestAction = !!appAuth?.action?.actionId;
  const showTestButton = (credential?.status !== 'valid' || hasBeenModified) && !isConnect && hasTestAction;
  const hasFilledRequiredArgs = useMemo(() => (requiredArgs?.length === 0 ? true : requiredArgs?.every((n) => Boolean(currentValue?.[n.key]))), [JSON.stringify(requiredArgs), currentValue]);
  const hasFilledRequiredOAuth2Args = useMemo(() => (requiredOAuth2Args?.length === 0 ? true : requiredOAuth2Args?.every((n) => Boolean(currentValue?.[n.key]))), [JSON.stringify(requiredOAuth2Args), currentValue]);

  const handleOauth2 = async ({ credentialId, args, reconnect = false }) => {
    if (isM2M && !args) {
      await getM2MOauthToken({ appSlug, draft: appVersion === 'draft', credentialId }, (data) => {
        if (data?.success) {
          sendToast({ message: 'You have successfully reconnected to the app.', type: 'success', duration: 4000 });
        } else {
          sendToast({ message: 'There was a problem re-authenticating with the app.', type: 'error', duration: 4000 });
        }
      });
    }

    if (!isM2M) {
      setPref('platformLoading', { isLoading: true, message: `Connecting to ${appName}...` });
      window.gravityHandleOAuth2Callback = (response) => {
        setPref('platformLoading', { isLoading: false });
        if (response?.success && response?.data?.credentialId) {
          getCredentialById(response?.data?.credentialId, { secrets: true, companyId, forceUpdate: true });
          if (typeof onSetActiveCredentialId === 'function') {
            onSetActiveCredentialId(response?.data?.credentialId);
          }
          return;
        }
        sendToast({ message: `There was a problem signing in to ${appName}.`, type: 'error', duration: 4000 });
      };

      const onClose = () => {
        getCredentialById(credentialId, { secrets: true, companyId, forceUpdate: true }, (res) => {
          const newCredential = res?.[0];
          if (newCredential?.status === 'valid') {
            sendToast({ message: `You have successfully signed in!`, type: 'success', duration: 4000 });
          } else {
            sendToast({ message: `There was a problem signing in to ${appName}.`, type: 'error', duration: 4000 });
          }
        });

        if (typeof onSetActiveCredentialId === 'function') {
          onSetActiveCredentialId(credentialId);
        }
      };

      if (reconnect || credentialId) {
        openOauthWindow({ appSlug, credentialId, companyId, draft: appVersion === 'draft', onClose });
      } else {
        setPref('platformLoading', { isLoading: false });
      }
    }
  };

  const handleSave = async () => {
    await waitForSyncReady();
    credentialsRef?.current?.onSave({ fullSecrets: currentValue }, () => {
      action('SET_ARGUMENT_OPTION_HAS_BEEN_MODIFIED', false);
      sendToast({ message: 'Credentials saved!', type: 'success' });
    });
  };

  const handleSaveAndTest = async () => {
    if (showTestButton) {
      setIsTesting(true);
    }
    if (hasBeenModified) {
      await handleSave();
    }
    if (!hasFilledRequiredArgs) {
      setIsTesting(false);
      return;
    }
    if (showTestButton) {
      await onTest();
      setIsTesting(false);
    }
  };

  const handleConnect = async (shouldSave = true) => {
    if (shouldSave && hasBeenModified) {
      setIsSaving(true);
      await handleSave();
      await waitForSyncReady();
      setIsSaving(false);
    }

    if (!hasFilledRequiredOAuth2Args) {
      return;
    }

    return handleOauth2({ credentialId: credential?.credentialId, args: requiredOAuth2Args.length });
  };

  const handleReconnect = async () => {
    return handleOauth2({ credentialId: credential?.credentialId, reconnect: true });
  };

  useEffect(() => {
    if (!loading && isOAuth2 && isConnect && oauth2Args.length === 0 && autoConnectOAuth) {
      handleConnect(false);
    }
  }, [credential?.credentialId, isOAuth2, isConnect, oauth2Args.length, autoConnectOAuth]);

  if (loading || !(showReconnectButton || isConnect || hasBeenModified || showTestButton)) {
    return null;
  }

  return (
    <Panel.Section headerless className={'credential-buttons'}>
      {(hasBeenModified || showTestButton) && !isConnect && (
        <Button className={'primary full-width'} size={'xl'} onClick={handleSaveAndTest} loading={isTesting} disabled={isTesting || !hasBeenModified}>
          {!hasFilledRequiredArgs ? 'Save Incomplete Connection' : hasBeenModified && showTestButton ? 'Save and Test Connection' : hasBeenModified ? 'Save Connection' : 'Test Connection'}
        </Button>
      )}
      {showReconnectButton && (
        <Button id={'reconnect-credential'} className={'full-width'} size={'xl'} icon={SlRefresh} onClick={handleReconnect}>
          Reconnect
        </Button>
      )}
      {isConnect && (
        <Button className={'full-width primary'} size={'xl'} icon={SlRefresh} onClick={handleConnect} loading={isSaving} disabled={isSaving}>
          {!hasFilledRequiredOAuth2Args ? 'Save Incomplete Connection to ' : hasBeenModified ? 'Save and Sign in to ' : 'Sign in to '}
          {appName}
        </Button>
      )}
    </Panel.Section>
  );
};

export default CredentialButtons;
